import React from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { recipes } from '../data/mockData';

const { 
  FiClock, FiThermometer, FiZap, FiCheckCircle, 
  FiChevronRight, FiCalendar, FiActivity, FiAlertTriangle, 
  FiPlus, FiInfo 
} = FiIcons;

const ActiveTimer = () => (
  <div className="panel col-span-1 md:col-span-2 lg:col-span-3 bg-steel-900 text-cream-100 border-none relative overflow-hidden shadow-2xl">
    <div className="absolute top-0 right-0 p-4 opacity-5">
      <SafeIcon icon={FiZap} className="text-9xl" />
    </div>
    <div className="p-8 relative z-10 flex flex-col md:flex-row justify-between items-start md:items-center gap-10">
      <div className="space-y-2">
        <div className="flex items-center gap-2 text-tomato-400 text-[10px] font-mono mb-2 uppercase tracking-[0.3em]">
          <span className="w-2 h-2 rounded-full bg-tomato-500 animate-pulse shadow-[0_0_10px_rgba(230,57,70,0.8)]"></span>
          Module Active: Preparation Phase
        </div>
        <h2 className="text-3xl font-bold tracking-tight text-white">Circuit-Seared Salmon</h2>
        <p className="text-steel-400 text-xs font-mono uppercase tracking-[0.2em]">Sequence 03 // Skin Sealing</p>
      </div>
      
      <div className="flex items-center gap-10 bg-steel-800/40 p-6 rounded-sm border border-steel-700/50 backdrop-blur-xl">
        <div className="text-center">
          <div className="text-steel-500 text-[9px] font-mono mb-2 tracking-[0.3em]">T-MINUS</div>
          <div className="text-5xl font-mono font-bold text-white tracking-widest">04:28</div>
        </div>
        <div className="h-16 w-px bg-steel-700/50"></div>
        <div className="flex flex-col gap-4">
          <div className="flex items-center gap-3 text-sm text-steel-200">
            <div className="p-1.5 bg-tomato-500/20 rounded-sm">
              <SafeIcon icon={FiThermometer} className="text-tomato-400" />
            </div>
            <span className="font-mono text-xl">400°F</span>
          </div>
          <div className="flex items-center gap-3 text-sm text-steel-200">
            <div className="p-1.5 bg-tomato-500/20 rounded-sm">
              <SafeIcon icon={FiZap} className="text-tomato-400" />
            </div>
            <span className="font-mono text-xl uppercase tracking-tighter">Oven X1</span>
          </div>
        </div>
      </div>
    </div>
    <div className="h-2 w-full bg-steel-800">
      <motion.div 
        className="h-full bg-gradient-to-r from-tomato-600 via-tomato-500 to-tomato-400 shadow-[0_0_10px_rgba(230,57,70,0.5)]"
        initial={{ width: '40%' }}
        animate={{ width: '68%' }}
        transition={{ duration: 3, ease: "linear", repeat: Infinity, repeatType: "reverse" }}
      />
    </div>
  </div>
);

const MetricCard = ({ title, value, sub, icon, trend, status }) => (
  <div className="panel p-6 flex flex-col justify-between group hover:border-steel-300 transition-all shadow-sm">
    <div className="flex justify-between items-start mb-8">
      <div className={`p-3 rounded-sm ${status === 'warning' ? 'bg-orange-100 text-orange-600' : 'bg-cream-200 text-steel-700'}`}>
        <SafeIcon icon={icon} className="text-xl" />
      </div>
      {trend && (
        <span className="text-[9px] font-mono font-bold text-green-600 bg-green-100 px-2 py-1 rounded-sm uppercase tracking-widest">
          {trend}
        </span>
      )}
    </div>
    <div>
      <div className="text-4xl font-mono font-bold text-steel-900 mb-1 tracking-tighter">{value}</div>
      <div className="text-[10px] font-bold text-steel-500 uppercase tracking-[0.2em]">{title}</div>
      {sub && <div className="text-[9px] text-steel-400 mt-3 font-mono uppercase tracking-wider border-t border-steel-100 pt-2">{sub}</div>}
    </div>
  </div>
);

export const Dashboard = ({ setActiveTab }) => {
  return (
    <motion.div 
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-10 pb-12"
    >
      <header className="flex justify-between items-end">
        <div>
          <h1 className="text-3xl font-bold text-steel-900 tracking-tight">Command Center</h1>
          <p className="text-steel-500 text-sm mt-2 flex items-center gap-2">
            <span className="w-1.5 h-1.5 rounded-full bg-green-500"></span>
            Grid Operational. Cycle 42 in progress.
          </p>
        </div>
        <div className="hidden md:flex gap-3">
          <button className="px-4 py-2 bg-white border border-steel-200 rounded-sm text-[10px] font-mono font-bold uppercase tracking-widest flex items-center gap-2 hover:bg-steel-50 transition-colors">
            <SafeIcon icon={FiPlus} /> New Cycle
          </button>
          <div className="px-4 py-2 bg-steel-900 text-white rounded-sm text-[10px] font-mono font-bold uppercase tracking-widest flex items-center gap-2">
            <span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse"></span>
            Sync Active
          </div>
        </div>
      </header>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
        <ActiveTimer />
        
        <MetricCard 
          title="Weekly Modules" 
          value="14" 
          sub="3/14 Successfully Processed"
          icon={FiCalendar} 
          trend="+2"
        />
        <MetricCard 
          title="Inventory Load" 
          value="82%" 
          sub="3 Critical Low Alerts"
          icon={FiActivity}
          status="warning"
        />
        
        <div className="panel col-span-1 md:col-span-2 lg:col-span-1 flex flex-col group overflow-hidden shadow-sm">
          <div className="panel-header bg-steel-900 text-white border-steel-800 tracking-[0.2em] text-[9px]">
            Next Scheduled Cycle
            <button onClick={() => setActiveTab('board')} className="text-tomato-400 hover:text-tomato-300 transition-colors">
              <SafeIcon icon={FiChevronRight} />
            </button>
          </div>
          <div className="p-6 flex gap-6 items-center flex-1">
            <div className="relative overflow-hidden rounded-sm w-28 h-28 flex-shrink-0 shadow-md">
              <img src={recipes[1].image} alt="Next meal" className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" />
            </div>
            <div>
              <div className="text-[9px] font-mono text-tomato-500 mb-2 font-bold tracking-[0.3em] uppercase underline underline-offset-4">LUNCH // 12:30 PM</div>
              <h3 className="font-bold text-steel-900 text-xl leading-tight mb-3">{recipes[1].title}</h3>
              <div className="flex gap-3">
                <span className="text-[9px] bg-steel-100 px-2 py-1 rounded-sm font-mono text-steel-600 uppercase font-bold">15m Prep</span>
                <span className="text-[9px] bg-steel-100 px-2 py-1 rounded-sm font-mono text-steel-600 uppercase font-bold">Easy</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div className="panel shadow-sm">
          <div className="panel-header uppercase tracking-[0.2em] text-[9px]">System Notifications</div>
          <div className="p-5 space-y-4">
            <div className="flex gap-4 items-start p-4 bg-orange-50 border border-orange-100 rounded-sm">
              <SafeIcon icon={FiAlertTriangle} className="text-orange-500 mt-1" />
              <div>
                <p className="text-xs font-bold text-orange-900 uppercase tracking-widest mb-1">Inventory Alert: Chicken Breast</p>
                <p className="text-[11px] text-orange-700 leading-relaxed">Stock level depleted. Tuesday Dinner (Kinetic Beef Stir-fry) requires immediate procurement or module adjustment.</p>
              </div>
            </div>
            <div className="flex gap-4 items-start p-4 bg-green-50 border border-green-100 rounded-sm">
              <SafeIcon icon={FiCheckCircle} className="text-green-500 mt-1" />
              <div>
                <p className="text-xs font-bold text-green-900 uppercase tracking-widest mb-1">Appliance Pre-heat Complete</p>
                <p className="text-[11px] text-green-700 leading-relaxed">Smart Oven X1 reached 400°F. Insertion window: 12:00m remaining. Sequence 03 ready.</p>
              </div>
            </div>
          </div>
        </div>

        <div className="panel shadow-sm">
          <div className="panel-header uppercase tracking-[0.2em] text-[9px]">Recent Logisticals</div>
          <div className="divide-y divide-steel-100">
            <div className="p-5 flex justify-between items-center hover:bg-steel-50 transition-colors group">
              <div className="flex items-center gap-3">
                <SafeIcon icon={FiInfo} className="text-steel-300 group-hover:text-tomato-500 transition-colors" />
                <span className="text-xs text-steel-700 font-bold uppercase tracking-wider">Grocery Hub Alpha Sync</span>
              </div>
              <span className="text-[9px] font-mono text-steel-400 uppercase">Today // 08:45</span>
            </div>
            <div className="p-5 flex justify-between items-center hover:bg-steel-50 transition-colors group">
              <div className="flex items-center gap-3">
                <SafeIcon icon={FiInfo} className="text-steel-300 group-hover:text-tomato-500 transition-colors" />
                <span className="text-xs text-steel-700 font-bold uppercase tracking-wider">Weekly Board Finalized</span>
              </div>
              <span className="text-[9px] font-mono text-steel-400 uppercase">Yesterday // 21:12</span>
            </div>
            <div className="p-5 flex justify-between items-center hover:bg-steel-50 transition-colors group">
              <div className="flex items-center gap-3">
                <SafeIcon icon={FiInfo} className="text-steel-300 group-hover:text-tomato-500 transition-colors" />
                <span className="text-xs text-steel-700 font-bold uppercase tracking-wider">Auto-Fill: 12 Ingredients</span>
              </div>
              <span className="text-[9px] font-mono text-steel-400 uppercase">Oct 14 // 18:10</span>
            </div>
          </div>
          <div className="p-4 bg-steel-50 text-center">
            <button className="text-[9px] font-mono font-bold uppercase tracking-widest text-steel-400 hover:text-tomato-500 transition-colors">View All Logs</button>
          </div>
        </div>
      </div>
      
      <div className="text-center pt-8 opacity-40">
        <p className="text-[10px] text-steel-400 font-medium tracking-tight">CookCircuit Unified Interface // Made by LingLink AI and DigLab AI</p>
      </div>
    </motion.div>
  );
};